/* global Android */

const html = require('choo/html');
const raw = require('choo/html/raw');
const QRCode = require('qrcode');
const assets = require('../../common/assets');
const {
  bytes,
  copyToClipboard,
  list,
  percent,
  platform,
  resolvePublicShareUrl,
  timeLeft
} = require('../utils');
const expiryOptions = require('./expiryOptions');

function expiryInfo(translate, archive) {
  const l10n = timeLeft(archive.expiresAt - Date.now());
  return raw(
    translate('archiveExpiryInfo', {
      downloadCount: translate('downloadCount', {
        num: archive.dlimit - archive.dtotal
      }),
      timespan: translate(l10n.id, l10n)
    })
  );
}

function password(state) {
  const MAX_LENGTH = 32;

  return html`
    <div class="mb-2 px-1">
      <input
        id="autocomplete-decoy"
        class="hidden"
        type="password"
        value="lol"
      />
      <div class="checkbox inline-block mr-3">
        <input
          id="add-password"
          type="checkbox"
          ${state.archive.password ? 'checked' : ''}
          autocomplete="off"
          onchange="${togglePasswordInput}"
        />
        <label for="add-password">
          ${state.translate('addPassword')}
        </label>
      </div>
      <input
        id="password-input"
        class="${state.archive.password
          ? ''
          : 'invisible'} border rounded focus:border-blue-60 leading-normal my-1 py-1 px-2 h-8 dark:bg-grey-80"
        autocomplete="off"
        maxlength="${MAX_LENGTH}"
        type="password"
        oninput="${inputChanged}"
        onfocus="${focused}"
        placeholder="${state.translate('unlockInputPlaceholder')}"
        value="${state.archive.password || ''}"
      />
      <label
        id="password-msg"
        for="password-input"
        class="block text-xs text-grey-70"
      ></label>
    </div>
  `;

  function togglePasswordInput(event) {
    event.stopPropagation();
    const checked = event.target.checked;
    const input = document.getElementById('password-input');
    if (checked) {
      input.classList.remove('invisible');
      input.focus();
    } else {
      input.classList.add('invisible');
      input.value = '';
      document.getElementById('password-msg').textContent = '';
      state.archive.password = null;
    }
  }

  function inputChanged() {
    const passwordInput = document.getElementById('password-input');
    const pwdmsg = document.getElementById('password-msg');
    const password = passwordInput.value;
    const length = password.length;

    if (length === MAX_LENGTH) {
      pwdmsg.textContent = state.translate('maxPasswordLength', {
        length: MAX_LENGTH
      });
    } else {
      pwdmsg.textContent = '';
    }
    state.archive.password = password;
  }

  function focused(event) {
    event.preventDefault();
    const el = document.getElementById('password-input');
    if (el.placeholder !== state.translate('unlockInputPlaceholder')) {
      el.placeholder = '';
    }
  }
}

function fileInfo(state, file, action, checkbox) {
  return html`
    <send-file class="flex flex-row items-center p-4 w-full bg-white dark:bg-grey-90 rounded-lg border border-grey-10 dark:border-grey-70 shadow-sm hover:shadow-md hover:border-blue-30 dark:hover:border-blue-70 transition-all duration-200 group relative overflow-hidden">
      <div class="absolute left-0 top-0 bottom-0 w-1 bg-blue-60 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-200"></div>
      
      ${checkbox ? html`<div class="mr-4 flex-shrink-0">${checkbox}</div>` : ''}

      <div class="flex items-center justify-center h-12 w-12 bg-blue-10 dark:bg-grey-80 rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
        <svg class="h-7 w-7 text-blue-60 dark:text-blue-40">
          <use xlink:href="${assets.get('blue_file.svg')}#icon"/>
        </svg>
      </div>
      
      <div class="ml-4 flex-grow min-w-0">
        <div class="flex items-center justify-between mb-1">
          <h1 class="text-sm font-bold text-grey-80 dark:text-grey-20 truncate pr-4" title="${file.webkitRelativePath || file.name}">
            ${file.webkitRelativePath || file.name}
          </h1>
          <span class="text-xs font-medium text-blue-60 dark:text-blue-40 opacity-0 group-hover:opacity-100 transition-opacity">
            ${file.uploading ? state.translate('fileUploadingStatus') : state.translate('fileReady')}
          </span>
        </div>
        
        <div class="flex items-center text-xs text-grey-50 dark:text-grey-40 space-x-3">
          <span class="bg-grey-10 dark:bg-grey-80 px-1.5 py-0.5 rounded">${bytes(file.size)}</span>
          <span class="flex items-center">
            <svg class="w-3 h-3 mr-1 opacity-60"><use xlink:href="${assets.get('lock.svg')}#icon"/></svg>
            ${state.translate('encryptionProtected')}
          </span>
        </div>
      </div>
      
      <div class="ml-4 flex-shrink-0">
        ${action}
      </div>
    </send-file>`;
}

function archiveInfo(archive, action) {
  return html`
    <p class="w-full flex items-center">
      <svg class="h-8 w-6 mr-3 flex-shrink-0 text-white dark:text-grey-90">
        <use xlink:href="${assets.get('blue_file.svg')}#icon"/>
      </svg>
      <p class="flex-grow">
        <h1 class="text-base font-medium word-break-all">${archive.name}</h1>
        <div class="text-sm font-normal opacity-75 pt-1">${bytes(
          archive.size
        )}</div>
      </p>
      ${action}
    </p>`;
}

function archiveDetails(state, translate, archive) {
  if (archive.manifest.files.length > 1) {
    return html`
      <details
        class="w-full pb-1"
        ${archive.open ? 'open' : ''}
        ontoggle="${toggled}"
      >
        <summary
          class="flex items-center link-blue text-sm cursor-pointer outline-none"
        >
          <svg
            class="fill-current w-4 h-4 mr-1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
          >
            <path
              d="M12.95 10.707l.707-.707L8 4.343 6.586 5.757 10.828 10l-4.242 4.243L8 15.657l4.95-4.95z"
            />
          </svg>
          ${translate('fileCount', {
            num: archive.manifest.files.length
          })}
        </summary>
        ${list(archive.manifest.files.map(f => fileInfo(state, f)))}
      </details>
    `;
  }
  function toggled(event) {
    event.stopPropagation();
    archive.open = event.target.open;
  }
}

module.exports = function(state, emit, archive) {
  const copyOrShare =
    state.capabilities.share || platform() === 'android'
      ? html`
          <button
            class="flex items-center text-sm font-semibold text-blue-60 hover:text-blue-70 dark:text-blue-40 dark:hover:text-blue-30 transition-colors"
            onclick=${share}
            title="${state.translate('shareLinkButton')}"
          >
            <svg class="h-4 w-4 mr-2 fill-current">
              <use xlink:href="${assets.get('share-24.svg')}#icon" />
            </svg>
            ${state.translate('shareLinkButton')}
          </button>
        `
      : html`
          <button
            class="flex items-center text-sm font-semibold text-blue-60 hover:text-blue-70 dark:text-blue-40 dark:hover:text-blue-30 transition-colors"
            onclick=${copy}
            title="${state.translate('copyLinkButton')}"
          >
            <svg class="h-4 w-4 mr-2 fill-current">
              <use xlink:href="${assets.get('copy-16.svg')}#icon" />
            </svg>
            ${state.translate('copyLinkButton')}
          </button>
        `;

  const dl =
    platform() === 'web'
      ? html`
          <a
            class="flex items-center text-sm font-semibold text-green-60 hover:text-green-70 dark:text-green-40 dark:hover:text-green-30 transition-colors"
            href="${archive.url}"
            title="${state.translate('downloadButtonLabel')}"
            tabindex="0"
          >
            <svg class="h-4 w-4 mr-2 fill-current">
              <use xlink:href="${assets.get('dl.svg')}#icon" />
            </svg>
            ${state.translate('downloadButtonLabel')}
          </a>
        `
      : html`<div></div>`;

  return html`
    <send-archive
      id="archive-${archive.id}"
      class="flex flex-col bg-white p-5 rounded-xl border border-grey-10 dark:border-grey-70 shadow-sm hover:shadow-md transition-all duration-200 dark:bg-grey-90 mb-4 group relative"
    >
      <div class="flex items-start justify-between w-full">
        <div class="flex items-center flex-grow min-w-0">
          <div class="h-10 w-10 bg-blue-10 dark:bg-grey-80 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
            <svg class="h-6 w-6 text-blue-60 dark:text-blue-40">
              <use xlink:href="${assets.get('blue_file.svg')}#icon"/>
            </svg>
          </div>
          <div class="min-w-0">
            <h1 class="text-base font-bold text-grey-80 dark:text-grey-10 truncate mb-1" title="${archive.name}">
              ${archive.name}
            </h1>
            <div class="flex items-center text-xs text-grey-50 dark:text-grey-40">
              <span class="font-medium">${bytes(archive.size)}</span>
              <span class="mx-2 opacity-50">·</span>
              <span>${expiryInfo(state.translate, archive)}</span>
            </div>
          </div>
        </div>
      </div>

      ${archiveDetails(state, state.translate, archive)}

      <div class="mt-4 pt-4 border-t border-grey-5/50 dark:border-grey-80 flex items-center justify-between">
        <div class="flex space-x-6">
          ${dl}
          ${copyOrShare}
        </div>
        <button
          class="flex items-center text-sm font-semibold text-red-60 hover:text-red-70 dark:text-red-40 dark:hover:text-red-30 transition-colors"
          onclick=${del}
          title="${state.translate('deleteButtonHover')}"
        >
          <svg class="h-4 w-4 mr-2 fill-current">
            <use xlink:href="${assets.get('close-16.svg')}#icon" />
          </svg>
          ${state.translate('deleteButtonHover')}
        </button>
      </div>
    </div>
  `;

  function copy(event) {
    event.stopPropagation();
    copyToClipboard(resolvePublicShareUrl(archive.url, state.baseUrl));
    const btn = event.currentTarget;
    const text = btn.lastChild;
    const originalText = text.textContent;
    text.textContent = state.translate('copiedUrl');
    btn.classList.add('text-green-60');
    setTimeout(() => {
      text.textContent = originalText;
      btn.classList.remove('text-green-60');
    }, 2000);
  }

  function del(event) {
    event.stopPropagation();
    if (confirm(state.translate('deleteConfirm'))) {
      emit('delete', archive);
    }
  }

  async function share(event) {
    event.stopPropagation();
    if (platform() === 'android') {
      Android.shareUrl(archive.url);
    } else {
      try {
        await navigator.share({
          title: state.translate('-send-brand'),
          text: `Download "${archive.name}" with Firefox Send: simple, safe file sharing`,
          url: archive.url
        });
      } catch (e) {
        // ignore
      }
    }
  }
};

module.exports.wip = function(state, emit) {
  const fileCount = state.archive.numFiles;
  
  // Initialize selection state
  if (!state.selectedFiles) {
    state.selectedFiles = new Set();
  }
  const selectedCount = state.selectedFiles.size;

  function toggleSelect(file) {
    if (state.selectedFiles.has(file)) {
      state.selectedFiles.delete(file);
    } else {
      state.selectedFiles.add(file);
    }
    emit('render');
  }

  function clearAll() {
    if (confirm(state.translate('clearAllConfirm'))) {
      emit('clearFiles');
    }
  }

  function deleteSelected() {
    if (confirm(state.translate('deleteSelectedConfirm', { count: selectedCount }))) {
      state.selectedFiles.forEach(f => state.archive.remove(f));
      state.selectedFiles.clear();
      if (state.archive.numFiles === 0) state.archive.clear();
      emit('render');
    }
  }

  function remove(file, title) {
    return html`
      <button
        class="text-red-60 hover:text-red-70 p-2 rounded-lg hover:bg-red-50/10 transition-colors ml-2"
        title="${title}"
        onclick="${(e) => {
          e.stopPropagation();
          if (confirm(state.translate('deleteConfirm'))) {
            emit('removeUpload', file);
            state.selectedFiles.delete(file);
          }
        }}"
      >
        <svg class="w-5 h-5 fill-current"><use xlink:href="${assets.get('close-16.svg')}#icon"/></svg>
      </button>
    `;
  }

  function renderCheckbox(file) {
    const isChecked = state.selectedFiles.has(file);
    // Unique ID for label
    const id = `select-${file.name}-${file.size}-${file.lastModified}`;
    return html`
      <div class="checkbox" onclick="${(e) => e.stopPropagation()}">
        <input 
          type="checkbox" 
          id="${id}" 
          ${isChecked ? 'checked' : ''} 
          onchange="${() => toggleSelect(file)}"
        />
        <label for="${id}"></label>
      </div>
    `;
  }
  
  return html`
    <send-upload-area
      class="flex flex-col bg-white p-8 md:p-12 rounded-2xl border border-grey-10 dark:border-grey-70 shadow-sm dark:bg-grey-90 w-full animate-fade-in"
      style="height: 60vh; min-height: 400px; max-height: 600px;"
    >
      <div class="flex items-center justify-between mb-8 px-2 flex-shrink-0">
        <div>
          <h2 class="text-xl font-bold text-grey-90 dark:text-grey-10 leading-[1.2]">
            ${state.translate('selectedFiles')}
            ${selectedCount > 0 ? html`<span class="text-blue-60 text-base ml-2">${state.translate('selectedFilesCount', { count: selectedCount })}</span>` : ''}
          </h2>
          <div class="text-xs font-medium text-grey-50 dark:text-grey-40 mt-1 uppercase tracking-wider">
            ${state.translate('fileCount', { num: fileCount })} · ${bytes(state.archive.size)}
          </div>
        </div>
        <div class="flex space-x-3 items-center">
          ${selectedCount > 0 ? html`
            <button 
              class="text-sm font-bold text-red-60 hover:text-red-70 bg-red-10 dark:bg-grey-80 px-4 py-2 rounded-xl transition-all duration-200"
              onclick="${deleteSelected}"
            >
              ${state.translate('deleteSelectedFiles')}
            </button>
          ` : ''}
          <button 
            class="text-sm font-bold text-red-60 hover:text-red-70 bg-red-10 dark:bg-grey-80 px-4 py-2 rounded-xl transition-all duration-200"
            onclick="${clearAll}"
          >
            ${state.translate('clearAllFiles')}
          </button>
          <button 
            class="text-sm font-bold text-blue-60 hover:text-blue-70 dark:text-blue-40 dark:hover:text-blue-30 flex items-center bg-blue-10 dark:bg-grey-80 px-4 py-2 rounded-xl transition-all duration-200"
            onclick="${() => document.getElementById('file-upload').click()}"
          >
            <svg class="w-4 h-4 mr-2 fill-current"><use xlink:href="${assets.get('add.svg')}#icon"/></svg>
            ${state.translate('addFilesButton')}
          </button>
        </div>
      </div>

      <input
        id="file-upload"
        class="hidden"
        type="file"
        multiple
        onchange="${add}"
      />

      <div class="flex-grow overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-grey-20 dark:scrollbar-thumb-grey-70 custom-scroll-container">
        ${list(
          Array.from(state.archive.files)
            .reverse()
            .map(f =>
              fileInfo(state, f, remove(f, state.translate('deleteButtonHover')), renderCheckbox(f))
            ),
          'space-y-4 py-2',
          'transition-all duration-200'
        )}
      </div>

      <div class="mt-4 pt-4 border-t border-grey-10 dark:border-grey-70 space-y-4 flex-shrink-0">
        <div class="bg-grey-10 dark:bg-grey-80 p-4 rounded-xl space-y-4">
          ${expiryOptions(state, emit)}
          ${password(state, emit)}
        </div>
        <button
          id="upload-btn"
          class="w-full py-4 bg-blue-60 hover:bg-blue-70 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:transform-none disabled:shadow-none"
          title="${state.translate('uploadButton')}"
          onclick="${upload}"
        >
          ${state.translate('uploadButton')}
        </button>
      </div>
    </send-upload-area>
  `;

  function upload(event) {
    window.scrollTo(0, 0);
    event.preventDefault();
    event.target.disabled = true;
    if (!state.uploading) {
      emit('upload');
    }
  }

  function add(event) {
    event.preventDefault();
    const newFiles = Array.from(event.target.files);
    emit('addFiles', { files: newFiles });
  }

  function remove(file, desc) {
    return html`
      <button
        class="p-2 text-grey-40 hover:text-red-60 dark:text-grey-50 dark:hover:text-red-40 transition-colors focus:outline-none rounded-full hover:bg-red-10 dark:hover:bg-red-90/20"
        onclick=${() => emit('removeUpload', file)}
        title="${desc}"
      >
        <svg class="h-5 w-5 fill-current"><use xlink:href="${assets.get('close-16.svg')}#icon"/></svg>
      </button>
    `;
  }
};

module.exports.uploading = function(state, emit) {
  const transfer = state.transfer;
  const progress = (transfer && transfer.progressRatio) || 0;
  const progressPercent = percent(progress);
  const isComplete = progress >= 1;
  const isError = state.uploading.error;
  const indefinite =
    transfer &&
    transfer.progressIndefinite &&
    !isComplete &&
    !isError;

  const titleKey = isError
    ? 'uploadError'
    : isComplete
      ? 'uploadComplete'
      : indefinite
        ? 'encryptingFile'
        : 'uploadingTitle';

  return html`
    <send-upload-area
      class="flex flex-col items-center justify-start bg-white dark:bg-grey-90 border border-grey-10 dark:border-grey-70 rounded-2xl p-8 md:p-12 h-full w-full shadow-sm animate-fade-in"
      style="height: 60vh; min-height: 400px; max-height: 600px;"
    >
      <div class="pt-4 flex flex-col items-center w-full">
        <div class="relative w-24 h-24 mb-8 flex items-center justify-center">
          <!-- Progress ring, spinner (encrypting), or status icon -->
          ${isError
            ? html`<svg class="w-full h-full text-red-60"><use xlink:href="${assets.get('error.svg')}#icon"/></svg>`
            : isComplete
              ? html`<svg class="w-full h-full text-green-60"><use xlink:href="${assets.get('completed.svg')}#icon"/></svg>`
              : indefinite
                ? html`
                    <div
                      class="send-upload-spinner rounded-full w-20 h-20 border-4 border-grey-10 dark:border-grey-80 border-t-blue-60 border-r-blue-60/30"
                      role="progressbar"
                      aria-valuetext="${state.translate('encryptingFile')}"
                    ></div>
                  `
                : html`
                <svg class="w-full h-full transform -rotate-90" aria-hidden="true">
                  <circle cx="48" cy="48" r="40" stroke="currentColor" stroke-width="6" fill="transparent" class="text-grey-10 dark:text-grey-80"/>
                  <circle cx="48" cy="48" r="40" stroke="currentColor" stroke-width="6" fill="transparent" stroke-dasharray="251.2" stroke-dashoffset="${251.2 * (1 - progress)}" class="text-blue-60 transition-all duration-500 ease-out"/>
                </svg>
                <div class="absolute inset-0 flex items-center justify-center text-xl font-bold text-blue-60 tracking-tighter">${progressPercent}</div>
              `
          }
        </div>

        <h2 class="text-2xl font-bold text-grey-90 dark:text-grey-10 mb-4 text-center leading-[1.2]">
          ${state.translate(titleKey)}
        </h2>
        
        <p class="text-base text-grey-60 dark:text-grey-40 text-center mb-3 px-6 truncate max-w-full font-medium">
          ${state.archive.name} <span class="ml-2 text-grey-40 dark:text-grey-50 font-normal">(${bytes(state.archive.size)})</span>
        </p>

        ${!isComplete && !isError
          ? html`
          <p class="text-sm text-grey-50 dark:text-grey-40 text-center mb-8 px-6 max-w-md leading-relaxed">
            ${indefinite
              ? state.translate('uploadProgressEncryptingHint')
              : state.translate('fileSizeProgress', transfer.sizes)}
          </p>
        `
          : ''}

        ${!isComplete && !isError ? html`
          <div class="w-full max-w-xs px-4">
            <div class="relative h-2.5 bg-grey-10 dark:bg-grey-80 rounded-full overflow-hidden shadow-inner">
              ${indefinite
                ? html`<div class="send-upload-indeterminate-fill"></div>`
                : html`<div class="absolute top-0 left-0 h-full bg-blue-60 transition-all duration-500 ease-out" style="width: ${progressPercent}"></div>`}
            </div>
            <div class="flex justify-between mt-4 text-xs font-bold text-grey-50 dark:text-grey-40 uppercase tracking-widest">
               <span>${indefinite ? state.translate('uploadProgressIndeterminateLabel') : state.translate('uploadingStatus')}</span>
               <span class="text-blue-60 font-mono">${indefinite ? '…' : progressPercent}</span>
            </div>
          </div>
        ` : ''}

        <button
          class="mt-12 px-8 py-3 rounded-xl font-bold transition-all duration-200 transform hover:-translate-y-0.5 ${isError || isComplete ? 'bg-blue-60 text-white hover:bg-blue-70 shadow-lg hover:shadow-xl' : 'text-red-60 hover:bg-red-50 dark:hover:bg-red-40/10'}"
          onclick=${isError || isComplete ? () => location.reload() : cancel}
        >
          ${isError || isComplete ? state.translate('okButton') : state.translate('deletePopupCancel')}
        </button>
      </div>
    </send-upload-area>
  `;

  function cancel() {
    emit('cancel');
  }
};

module.exports.empty = function(state, emit) {
  const upsell =
    state.user.loggedIn || !state.capabilities.account
      ? ''
      : html`
          <button
            class="center font-medium text-sm link-blue mt-4 mb-2"
            onclick="${event => {
              event.stopPropagation();
              emit('signup-cta', 'drop');
            }}"
          >
            ${state.translate('signInSizeBump', {
              size: bytes(state.LIMITS.MAX_FILE_SIZE)
            })}
          </button>
        `;

  function onDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    const overlay = document.getElementById('drag-overlay');
    if (overlay) overlay.classList.remove('hidden');
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    const overlay = document.getElementById('drag-overlay');
    if (overlay && !e.currentTarget.contains(e.relatedTarget)) {
       overlay.classList.add('hidden');
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const overlay = document.getElementById('drag-overlay');
    if (overlay) overlay.classList.add('hidden');
    
    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        emit('addFiles', { files: Array.from(e.dataTransfer.files) });
    }
  }

  return html`
    <send-upload-area
      class="relative flex flex-col items-center justify-start bg-white dark:bg-grey-90 border-2 border-dashed border-grey-10 dark:border-grey-70 rounded-2xl p-8 md:p-12 h-full w-full transition-all duration-300 hover:border-blue-40 dark:hover:border-blue-60 hover:bg-blue-5/30 dark:hover:bg-blue-90/5 shadow-sm overflow-y-auto"
      style="height: 60vh; min-height: 400px; max-height: 600px;"
      ondragenter="${onDragEnter}"
      ondragleave="${onDragLeave}"
      ondragover="${e => e.preventDefault()}"
      ondrop="${onDrop}"
      onclick="${e => {
        if (e.target.tagName !== 'LABEL' && !e.target.closest('button') && !e.target.closest('.relative')) {
          document.getElementById('file-upload').click();
        }
      }}"
    >
      <!-- Drag Overlay -->
      <div id="drag-overlay" class="hidden absolute inset-0 bg-blue-10 z-50 flex items-center justify-center rounded-2xl pointer-events-none opacity-90">
         <div class="text-xl font-bold text-blue-80">
            ${state.translate('uploadReleaseToUpload')}
         </div>
      </div>

      <div class="pt-2 flex flex-col items-center w-full">
        <div class="mb-8 p-3 bg-blue-10 dark:bg-grey-80 rounded-full">
          <svg class="w-10 h-10 md:w-12 md:h-12 text-blue-60">
            <use xlink:href="/${assets.get('addfiles.svg')}#plus" />
          </svg>
        </div>
        
        <h2 class="text-2xl md:text-3xl font-bold text-grey-90 dark:text-grey-10 mb-6 text-center leading-[1.2] tracking-tight">
          ${state.translate('uploadDragDropInstruction')}
        </h2>
        
        <div class="space-y-[1.5em] mb-10 text-center">
          <p class="text-base md:text-lg text-grey-60 dark:text-grey-40 leading-[1.5]">
            ${state.translate('uploadMaxFileSize', { size: bytes(state.user.maxSize) })}
          </p>
          <p class="text-xs text-grey-50 dark:text-grey-50 font-medium uppercase tracking-widest">
            ${state.translate('uploadFileTypes', { file_types: 'ANY' })}
          </p>
        </div>

        <div class="flex items-center justify-center mb-10 px-5 py-2.5 bg-green-10 dark:bg-grey-80 rounded-xl">
           <svg class="w-5 h-5 mr-3 text-green-60"><use xlink:href="${assets.get('lock.svg')}#icon"/></svg>
           <span class="text-sm font-bold text-green-70 dark:text-green-40 uppercase tracking-widest">${state.translate('uploadSecureTransfer')}</span>
        </div>

        <div class="relative w-full max-w-xs">
          <button
            type="button"
            class="w-full py-4 bg-blue-60 hover:bg-blue-70 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center group"
            onclick="${toggleDropdown}"
          >
            <span>${state.translate('selectFilesToUpload')}</span>
            <svg class="ml-2 h-5 w-5 transform group-hover:translate-y-0.5 transition-transform" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          
          <div id="upload-dropdown-menu" class="hidden absolute left-0 right-0 mt-2 rounded-xl shadow-2xl bg-white dark:bg-grey-80 ring-1 ring-black ring-opacity-5 z-20 overflow-hidden animate-fade-in border border-grey-10 dark:border-grey-70">
            <div class="p-1" role="menu">
              <button onclick="${triggerFileUpload}" class="w-full text-left px-4 py-3 text-sm font-medium text-grey-70 dark:text-grey-20 hover:bg-blue-10 dark:hover:bg-grey-70 rounded-lg flex items-center transition-colors">
                <svg class="w-4 h-4 mr-3 text-blue-60"><use xlink:href="${assets.get('add.svg')}#icon"/></svg>
                ${state.translate('selectFile')}
              </button>
              <button onclick="${triggerFolderUpload}" class="w-full text-left px-4 py-3 text-sm font-medium text-grey-70 dark:text-grey-20 hover:bg-blue-10 dark:hover:bg-grey-70 rounded-lg flex items-center transition-colors">
                <svg class="w-4 h-4 mr-3 text-blue-60"><use xlink:href="${assets.get('add.svg')}#icon"/></svg>
                ${state.translate('selectFolder')}
              </button>
            </div>
          </div>
        </div>

        <input
          id="file-upload"
          class="hidden"
          type="file"
          multiple
          onchange="${add}"
        />
        <input
          id="folder-upload"
          class="hidden"
          type="file"
          webkitdirectory
          directory
          multiple
          onchange="${add}"
        />

        <p class="mt-12 text-xs text-grey-50 dark:text-grey-50 text-center max-w-[280px] leading-relaxed italic">
          * ${state.translate('trustWarningMessage')}
        </p>
        ${upsell}
      </div>
    </send-upload-area>
  `;

  function focus(event) {
    event.target.nextElementSibling.classList.add('bg-blue-70', 'outline');
  }

  function blur(event) {
    event.target.nextElementSibling.classList.remove('bg-blue-70', 'outline');
  }

  function add(event) {
    event.preventDefault();
    const newFiles = Array.from(event.target.files);

    emit('addFiles', { files: newFiles });
  }

  function toggleDropdown(event) {
    event.stopPropagation();
    event.preventDefault();
    const menu = document.getElementById('upload-dropdown-menu');
    menu.classList.toggle('hidden');

    if (!menu.classList.contains('hidden')) {
      const closeMenu = (e) => {
        if (!event.target.contains(e.target)) {
          menu.classList.add('hidden');
          document.removeEventListener('click', closeMenu);
        }
      };
      setTimeout(() => document.addEventListener('click', closeMenu), 0);
    }
  }

  function triggerFileUpload(event) {
    event.stopPropagation();
    event.preventDefault();
    document.getElementById('file-upload').click();
    document.getElementById('upload-dropdown-menu').classList.add('hidden');
  }

  function triggerFolderUpload(event) {
    event.stopPropagation();
    event.preventDefault();
    document.getElementById('folder-upload').click();
    document.getElementById('upload-dropdown-menu').classList.add('hidden');
  }
};

module.exports.preview = function(state, emit) {
  const archive = state.fileInfo;
  if (archive.open === undefined) {
    archive.open = true;
  }
  return html`
    <send-archive class="flex flex-col items-center w-full bg-transparent">
      <div class="w-full max-w-[800px] mb-4">
        ${archiveInfo(archive)}
      </div>

      <div
        class="relative bg-white dark:bg-grey-90 dark:border dark:border-grey-70 flex items-stretch justify-center mb-6"
        style="
          width: 100%;
          max-width: 800px;
          aspect-ratio: 4/3;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        "
      >
        <div class="w-full h-full flex flex-col">
          <div class="text-sm text-grey-60 mb-2 flex-shrink-0">
            ${state.translate('downloadConfirmDescription')}
          </div>
          <div class="flex-1 min-h-0">
            ${list(
              archive.manifest.files.map(f => fileInfo(state, f)),
              'overflow-y-auto h-full scrollbar-thin scrollbar-thumb-grey-30 dark:scrollbar-thumb-grey-60 pr-1',
              'border-b last:border-b-0 border-grey-20 dark:border-grey-70 hover:bg-white dark:hover:bg-grey-70 transition-colors duration-150'
            )}
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center w-full max-w-[800px] z-10">
        <div class="checkbox inline-block mx-auto mb-2">
          <input
            id="trust-download"
            type="checkbox"
            autocomplete="off"
            onchange="${toggleDownloadEnabled}"
          />
          <label for="trust-download">
            ${state.translate('downloadTrustCheckbox', {
              count: archive.manifest.files.length
            })}
          </label>
        </div>
        <button
          id="download-btn"
          disabled
          class="btn rounded-lg w-full flex-shrink-0 focus:outline disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          title="${state.translate('downloadButtonLabel')}"
          onclick=${download}
        >
          ${state.translate('downloadButtonLabel')}
        </button>

        <button
          id="copy-btn"
          disabled
          class="btn rounded-lg w-full flex-shrink-0 mt-3 bg-blue-60 hover:bg-blue-70 text-white transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          onclick="${copyLink}"
          title="${state.translate('copyLinkButton')}"
        >
          ${state.translate('copyLinkButton')}
        </button>

        <button
          id="scan-btn"
          disabled
          class="btn rounded-lg w-full flex-shrink-0 mt-3 bg-blue-60 hover:bg-blue-70 text-white transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          onclick="${showQrCode}"
          title="${state.translate('scanQrCodeButton')}"
        >
          ${state.translate('scanQrCodeButton')}
        </button>

        <div class="flex flex-row justify-between items-center w-full mt-4">
          <div class="text-xs text-grey-60 text-left flex flex-row items-center">
            <span>${state.translate('linkExpiryLabel')}</span>
            <span class="ml-1" onload=${initTimer}>
              ${formatExpiry(archive.expiresAt)}
            </span>
          </div>
          <button 
            class="text-xs text-red-60 hover:text-red-70 cursor-pointer focus:outline-none"
            onclick="${confirmDelete}"
            title="${state.translate('deleteButtonHover')}"
          >
            ${state.translate('deleteButtonHover')}
          </button>
        </div>
      </div>
    </send-archive>
  `;

  function toggleDownloadEnabled(event) {
    event.stopPropagation();
    const checked = event.target.checked;
    const downloadBtn = document.getElementById('download-btn');
    const copyBtn = document.getElementById('copy-btn');
    const scanBtn = document.getElementById('scan-btn');
    if (downloadBtn) downloadBtn.disabled = !checked;
    if (copyBtn) copyBtn.disabled = !checked;
    if (scanBtn) scanBtn.disabled = !checked;
  }

  function download(event) {
    event.preventDefault();
    event.target.disabled = true;
    emit('download', archive);
  }

  function confirmDelete(event) {
    event.stopPropagation();
    event.preventDefault();
    if (confirm(state.translate('deleteConfirm'))) {
      const owned = state.storage.getFileById(archive.id);
      if (owned) {
        emit('delete', owned);
      } else {
        alert(state.translate('deleteErrorOwner'));
      }
    }
  }

  function initTimer(el) {
    const update = () => {
      if (archive.expiresAt) {
        el.textContent = formatExpiry(archive.expiresAt);
      } else {
        el.textContent = '...';
      }
    };
    const timer = setInterval(update, 1000);
    // Initial update
    update();
    el.onunload = () => clearInterval(timer);
  }

  function formatExpiry(expiresAt) {
    if (!expiresAt) return '';
    const remaining = expiresAt - Date.now();
    if (remaining <= 0) return state.translate('linkExpiredAlt');
    
    const minutes = Math.floor(remaining / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return state.translate('expiresHoursMinutes', {
        hours,
        minutes: mins
      });
    }
    return state.translate('expiresMinutes', { minutes: mins });
  }

  function copyLink(event) {
    event.stopPropagation();
    event.preventDefault();
    copyToClipboard(resolvePublicShareUrl(window.location.href, state.baseUrl));
    const btn = event.currentTarget;
    const originalText = btn.textContent;
    btn.textContent = state.translate('copiedUrl');
    setTimeout(() => {
      btn.textContent = originalText;
    }, 2000);
  }

  function showQrCode(event) {
    event.stopPropagation();
    const url = resolvePublicShareUrl(window.location.href, state.baseUrl);
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 opacity-0';
    
    const content = document.createElement('div');
    content.className = 'bg-white dark:bg-grey-90 rounded-xl p-8 flex flex-col items-center shadow-2xl transform scale-95 transition-transform duration-300 w-full max-w-[340px] mx-4';
    
    const title = document.createElement('h3');
    title.className = 'text-2xl font-bold mb-6 dark:text-grey-10';
    title.textContent = state.translate('scanQrCodeButton');
    content.appendChild(title);
    
    const canvas = document.createElement('canvas');
    QRCode.toCanvas(canvas, url, { width: 220, margin: 1 }, function (error) {
      if (error) console.error(error);
    });
    content.appendChild(canvas);
    
    const tip = document.createElement('p');
    tip.className = 'text-sm text-grey-60 dark:text-grey-40 mt-6 mb-8 text-center';
    tip.textContent = state.translate('qrcodeExpiry');
    content.appendChild(tip);
    
    const btnGroup = document.createElement('div');
    btnGroup.className = 'flex space-x-4 w-full';
    
    const saveBtn = document.createElement('button');
    saveBtn.className = 'flex-1 bg-blue-60 hover:bg-blue-70 text-white font-medium py-3 rounded-lg focus:outline-none transition-colors text-base whitespace-no-wrap shadow-sm';
    saveBtn.textContent = state.translate('saveImage');
    saveBtn.onclick = function() {
      const link = document.createElement('a');
      link.download = 'qrcode.png';
      link.href = canvas.toDataURL();
      link.click();
    };
    btnGroup.appendChild(saveBtn);
    
    const closeBtn = document.createElement('button');
    closeBtn.className = 'flex-1 bg-grey-10 hover:bg-grey-20 text-grey-90 font-medium py-3 rounded-lg focus:outline-none transition-colors text-base whitespace-no-wrap shadow-sm';
    closeBtn.textContent = state.translate('closeButton');
    closeBtn.onclick = closeModal;
    btnGroup.appendChild(closeBtn);
    
    content.appendChild(btnGroup);
    modal.appendChild(content);
    document.body.appendChild(modal);
    
    requestAnimationFrame(() => {
      modal.classList.remove('opacity-0');
      content.classList.remove('scale-95');
      content.classList.add('scale-100');
    });
    
    function closeModal() {
      modal.classList.add('opacity-0');
      content.classList.remove('scale-100');
      content.classList.add('scale-95');
      setTimeout(() => {
        document.body.removeChild(modal);
      }, 300);
    }
  }
};

module.exports.downloading = function(state) {
  const archive = state.fileInfo;
  const progress = state.transfer.progressRatio;
  const progressPercent = percent(progress);
  return html`
    <send-archive
      class="flex flex-col bg-white rounded shadow-light p-4 w-full max-w-sm md:w-128 dark:bg-grey-90"
    >
      ${archiveInfo(archive)}
      <div class="link-blue text-sm font-medium mt-2">
        ${progressPercent}
      </div>
      <progress class="my-3 w-full" value="${progress}">${progressPercent}</progress>
    </send-archive>
  `;
};
